<template>
    <div :class="containerClasses">
        <v-btn
            v-if="!isLoggedIn && !providerOnly"
            color="primary"
            variant="outlined"
            rounded="pill"
            :prepend-icon="mdiAccountCircleOutline"
            :block="mode === 'drawer'"
            class="core-google-login__signin-btn"
            @click="openAuthDialog"
        >
            Sign in
        </v-btn>
        <div v-else-if="!isLoggedIn" ref="providerShell" class="core-google-login__shell">
            <div class="core-google-login__visual" aria-hidden="true">
                <div class="core-google-login__icon-box">
                    <svg
                        class="core-google-login__icon-svg"
                        viewBox="0 0 18 18"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M17.64 9.2045c0-.6382-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7963 2.7164v2.2582h2.9082c1.7018-1.5664 2.6845-3.8727 2.6845-6.6155z"
                            fill="#4285F4"
                        />
                        <path
                            d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1791l-2.9082-2.2582c-.8059.54-1.8368.8591-3.0482.8591-2.3441 0-4.3282-1.5827-5.0364-3.7091H.9573v2.3318C2.4382 15.9827 5.4818 18 9 18z"
                            fill="#34A853"
                        />
                        <path
                            d="M3.9636 10.7127C3.7836 10.1727 3.6818 9.5954 3.6818 9s.1018-1.1727.2818-1.7127V4.9555H.9573C.3477 6.1709 0 7.5473 0 9s.3477 2.8291.9573 4.0445l3.0063-2.3318z"
                            fill="#FBBC05"
                        />
                        <path
                            d="M9 3.5782c1.3214 0 2.5077.4541 3.4405 1.3459l2.5813-2.5814C13.4632.8918 11.4259 0 9 0 5.4818 0 2.4382 2.0173.9573 4.9555l3.0063 2.3318C4.6718 5.1609 6.6559 3.5782 9 3.5782z"
                            fill="#EA4335"
                        />
                    </svg>
                </div>
                <span class="core-google-login__label">{{ normalizedProviderLabel }}</span>
            </div>
            <GoogleLogin
                :key="googleButtonKey"
                :callback="googleLogin"
                :button-config="googleButtonConfig"
                class="core-google-login__overlay"
                :style="{ transform: `scale(${providerShellWidth / 400}, 1.15)` }"
            />
        </div>
        <v-menu
            v-else-if="currentUser"
            v-model="menuOpen"
            width="300"
            max-width="calc(100vw - 24px)"
            location="bottom end"
            :offset="10"
        >
            <template v-slot:activator="{ props }">
                <v-btn
                    icon
                    variant="text"
                    size="44"
                    v-bind="props"
                    class="core-google-login__avatar-btn"
                    :aria-label="accountMenuAriaLabel"
                >
                    <v-avatar :color="avatarBackgroundColor" size="40" class="core-google-login__avatar">
                        <v-img
                            v-if="currentUser.picture"
                            :src="currentUser.picture"
                            :alt="`${currentUser.name} avatar`"
                            :class="avatarImageClasses"
                            cover
                        ></v-img>
                        <span v-else>{{ currentUserInitials }}</span>
                    </v-avatar>
                    <span
                        v-if="unreadAnnouncementCount > 0"
                        class="core-google-login__announcement-badge"
                        aria-hidden="true"
                    >
                        <v-icon :icon="mdiBellOutline" size="10"></v-icon>
                        <span>{{ unreadAnnouncementLabel }}</span>
                    </span>
                </v-btn>
            </template>
            <v-card class="core-google-login__menu" elevation="0">
                <div class="core-google-login__identity">
                    <v-avatar :color="avatarBackgroundColor" size="42" class="core-google-login__avatar core-google-login__avatar--menu">
                        <v-img
                            v-if="currentUser.picture"
                            :src="currentUser.picture"
                            :alt="`${currentUser.name} avatar`"
                            :class="avatarImageClasses"
                            referrerpolicy="no-referrer"
                            cover
                        ></v-img>
                        <span v-else>{{ currentUserInitials }}</span>
                    </v-avatar>
                    <div class="core-google-login__identity-copy">
                        <h3 class="core-google-login__name">{{ currentUser.name || 'Your account' }}</h3>
                        <p class="core-google-login__email">{{ currentUser.email }}</p>
                    </div>
                </div>
                <div class="core-google-login__actions">
                    <v-btn block variant="text" :prepend-icon="mdiCogOutline" class="core-google-login__menu-btn" @click="goToSettings">
                        Settings
                    </v-btn>
                    <v-btn
                        v-if="premiumSubscriptionPagesEnabled"
                        block
                        variant="text"
                        :prepend-icon="mdiCreditCardOutline"
                        class="core-google-login__menu-btn"
                        @click="goToBilling"
                    >
                        Subscription &amp; billing
                    </v-btn>
                    <v-btn
                        block
                        variant="text"
                        :prepend-icon="mdiBellOutline"
                        class="core-google-login__menu-btn"
                        @click="goToAnnouncements"
                    >
                        <span>Announcements</span>
                        <span
                            v-if="unreadAnnouncementCount > 0"
                            class="core-google-login__menu-count"
                        >
                            {{ unreadAnnouncementLabel }}
                        </span>
                    </v-btn>
                    <v-btn block variant="text" :prepend-icon="mdiAccountCircleOutline" class="core-google-login__menu-btn" @click="goToProfileImage">
                        Edit Profile Image
                    </v-btn>
                </div>
                <div class="core-google-login__logout">
                    <v-btn block variant="text" :prepend-icon="mdiLogout" class="core-google-login__menu-btn" @click="googleLogout">
                        Logout
                    </v-btn>
                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
    import { mdiAccountCircleOutline, mdiBellOutline, mdiCogOutline, mdiCreditCardOutline, mdiLogout } from '@mdi/js';
    import { googleLogout } from "vue3-google-login"
    import {
        loginWithExtensionSignupGoogleSessionRequest,
        loginWithGoogleSessionRequest,
        logoutUserSessionRequest
    } from '../services/apiServices';
    import { createAnalyticsEventId, getAnalyticsBrowserContext, trackAdvertisingStandardEvent } from '../services/analytics';
    import { useAuthPrompt } from '../store/authPrompt.store';
    import { useAnnouncements } from '../store/announcement.store';
    import { getCurrentSeasonPath, getPostLoginLandingPath, usePreferences } from '../store/preferences.store';
    import { useUser } from '../store/user.store';
    import { useNotification } from '../store/notification.store';
    import { isPremiumSubscriptionPagesEnabled } from '../../modules/subscriptions/config/premiumCatalog';
    import { computed, nextTick } from 'vue';
    import { useTheme } from 'vuetify';
    import { storeToRefs } from 'pinia'

    export default {
        name: 'CoreGoogleLogin',
        props: {
            mode: {
                type: String,
                default: 'bar'
            },
            stayOnCurrentRouteOnLogin: {
                type: Boolean,
                default: false
            },
            providerOnly: {
                type: Boolean,
                default: false
            },
            providerLabel: {
                type: String,
                default: 'Sign in with Google'
            },
            authSurface: {
                type: String,
                default: 'default'
            }
        },
        data: function() {
            return {
                isSubmitting: false,
                menuOpen: false,
                providerShellWidth: 400,
            }
        },
        computed: {
            currentUser() {
                return this.userProfile || null;
            },
            premiumSubscriptionPagesEnabled() {
                return isPremiumSubscriptionPagesEnabled();
            },
            currentUserInitials() {
                const rawName = `${this.currentUser?.name || ''}`.trim();
                const rawEmail = `${this.currentUser?.email || ''}`.trim();
                const nameParts = rawName
                    .split(/\s+/)
                    .map((part) => part.replace(/[^A-Za-z0-9]/g, ''))
                    .filter(Boolean);
                const initials = nameParts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
                if (initials) {
                    return initials;
                }
                return (rawEmail.charAt(0) || 'A').toUpperCase();
            },
            isCharacterAvatar() {
                return `${this.currentUser?.avatarSource || ''}`.trim() === 'character';
            },
            avatarBackgroundColor() {
                return this.currentUser?.picture ? 'surface-bright' : 'primary';
            },
            avatarImageClasses() {
                return {
                    'core-google-login__avatar-image': true,
                    'core-google-login__avatar-image--character': this.isCharacterAvatar
                };
            },
            containerClasses() {
                return {
                    'core-google-login': true,
                    'core-google-login--drawer': this.mode === 'drawer',
                    'core-google-login--provider': this.providerOnly,
                    'core-google-login--light': !this.isDarkMode
                };
            },
            googleButtonConfig() {
                return {
                    type: 'standard',
                    theme: 'outline',
                    size: 'large',
                    text: this.googleButtonText,
                    shape: 'rectangular',
                    width: 400,
                    logo_alignment: 'left'
                };
            },
            googleButtonKey() {
                return `${this.mode}:${this.googleButtonText}`;
            },
            googleButtonText() {
                if (this.normalizedProviderLabel === 'Sign up with Google') return 'signup_with';
                if (this.normalizedProviderLabel === 'Continue with Google') return 'continue_with';
                return 'signin_with';
            },
            normalizedProviderLabel() {
                return `${this.providerLabel || ''}`.trim() || 'Sign in with Google';
            },
            isExtensionSignupSurface() {
                return `${this.authSurface || ''}`.trim().toLowerCase() === 'extension-signup';
            },
            unreadAnnouncementCount() {
                const parsed = Number.parseInt(this.unreadCount, 10);
                return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
            },
            unreadAnnouncementLabel() {
                return this.unreadAnnouncementCount > 99
                    ? '99+'
                    : `${this.unreadAnnouncementCount}`;
            },
            accountMenuAriaLabel() {
                if (this.unreadAnnouncementCount === 0) {
                    return 'Open account menu';
                }
                const suffix = this.unreadAnnouncementCount === 1
                    ? 'announcement'
                    : 'announcements';
                return `Open account menu, ${this.unreadAnnouncementCount} unread ${suffix}`;
            }
        },
        mounted() {
            const shell = this.$refs.providerShell;
            if (!shell) return;
            // Keep the existing Google credential button interactive across the full
            // custom surface, including widths beyond Google's 400px rendering cap.
            this.providerResizeObserver = new ResizeObserver(([entry]) => {
                const width = entry?.contentRect.width;
                if (!Number.isFinite(width) || width <= 0) return;
                cancelAnimationFrame(this.providerResizeFrame);
                // Vue must not update layout inside ResizeObserver's delivery cycle.
                this.providerResizeFrame = requestAnimationFrame(() => {
                    this.providerResizeFrame = null;
                    if (this.isLoggedIn || !shell.isConnected || this.$refs.providerShell !== shell) return;
                    if (Math.abs(this.providerShellWidth - width) < 0.5) return;
                    this.providerShellWidth = width;
                });
            });
            this.providerResizeObserver.observe(shell);
        },
        beforeUnmount() {
            this.stopProviderResizeObservation();
        },
        methods: {
            stopProviderResizeObservation() {
                this.providerResizeObserver?.disconnect();
                cancelAnimationFrame(this.providerResizeFrame);
                this.providerResizeFrame = null;
            },
            async routeToPreferredLanding({ replace = false } = {}) {
                const targetPath = getPostLoginLandingPath(this.postLoginLanding);
                if (!targetPath || targetPath === '/' || this.$route?.path === targetPath) {
                    return;
                }

                const navigate = replace ? this.$router.replace : this.$router.push;
                await navigate.call(this.$router, targetPath);
            },
            goToSettings() {
                this.menuOpen = false;
                if (this.$route?.path === '/settings/') {
                    return;
                }
                this.$router.push('/settings/');
            },
            goToBilling() {
                this.menuOpen = false;
                if (this.$route?.path === '/account/billing/') {
                    return;
                }
                this.$router.push('/account/billing/');
            },
            goToProfileImage() {
                this.menuOpen = false;
                if (this.$route?.path === '/profile/image/') {
                    return;
                }
                this.$router.push('/profile/image/');
            },
            goToAnnouncements() {
                this.menuOpen = false;
                if (this.$route?.path === '/announcements/') {
                    return;
                }
                this.$router.push('/announcements/');
            },
            openAuthDialog() {
                this.authPrompt.openLoginDialog({
                    context: 'general',
                    stayOnCurrentRouteOnLogin: this.stayOnCurrentRouteOnLogin
                });
            },
            async googleLogin(response) {
                if (!response?.credential || this.isSubmitting) {
                    return null;
                }

                this.isSubmitting = true;
                try {
                    const isExtensionSignupRequest = this.isExtensionSignupSurface;
                    const authEventId = isExtensionSignupRequest
                        ? ''
                        : createAnalyticsEventId('auth');
                    let sessionData = null;
                    if (isExtensionSignupRequest) {
                        sessionData = await loginWithExtensionSignupGoogleSessionRequest({
                            credential: response.credential
                        });
                    } else {
                        const browserContext = getAnalyticsBrowserContext();
                        sessionData = await loginWithGoogleSessionRequest({
                            credential: response.credential,
                            analyticsEventId: authEventId,
                            analyticsSessionId: browserContext.sessionId,
                            redditUuid: browserContext.redditUuid,
                            adAttribution: browserContext.adAttribution,
                            eventSourceUrl: browserContext.eventSourceUrl,
                            pagePath: browserContext.pagePath,
                            screenWidth: browserContext.screenWidth,
                            screenHeight: browserContext.screenHeight
                        });
                    }

                    if (!sessionData?.user || !sessionData?.token) {
                        throw new Error('Invalid Google session response');
                    }

                    const authTracking = sessionData?.authTracking || null;
                    if (!isExtensionSignupRequest && authTracking?.eventName) {
                        trackAdvertisingStandardEvent({
                            eventName: authTracking.eventName,
                            eventId: authTracking.eventId || authEventId,
                            properties: {
                                source: 'google_oauth'
                            }
                        });
                    }

                    this.userStore.setSession({
                        user: sessionData.user,
                        token: sessionData.token
                    });

                    await nextTick();
                    if (!this.stayOnCurrentRouteOnLogin) {
                        await this.routeToPreferredLanding({ replace: true });
                    }
                } catch (err) {
                    console.log(err);
                    this.notificationState.notify({ message: 'Google sign in failed. Please try again.' });
                    return null
                } finally {
                    this.isSubmitting = false;
                }
            },
            async googleLogout() {
                try {
                    await logoutUserSessionRequest();
                } catch (err) {
                    console.log(err);
                } finally {
                    this.menuOpen = false;
                    try {
                        await googleLogout();
                    } catch (_err) {
                        // Google logout is best-effort cleanup only.
                    }
                    this.userStore.clearSession();
                    if (this.$route?.meta?.requiresAuth) {
                        this.$router.push(getCurrentSeasonPath());
                    }
                }
            }
        },
        watch: {
            isLoggedIn(newValue, oldValue) {
                if (newValue) this.stopProviderResizeObservation();
                if (!newValue || oldValue) {
                    return;
                }
                if (this.stayOnCurrentRouteOnLogin || this.authPrompt.stayOnCurrentRouteOnLogin || this.$route?.path !== '/') {
                    return;
                }
                this.routeToPreferredLanding({ replace: true });
            }
        },
        setup() {
            const userState = useUser();
            const { isLoggedIn, currentUser: userProfile } = storeToRefs(userState);
            const preferencesState = usePreferences();
            const { postLoginLanding } = storeToRefs(preferencesState);
            const authPrompt = useAuthPrompt();
            const announcementState = useAnnouncements();
            const { unreadCount } = storeToRefs(announcementState);
            const notificationState = useNotification();
            const theme = useTheme();
            const isDarkMode = computed(() => theme.global.name.value === 'dark');
            return {
                mdiAccountCircleOutline,
                mdiBellOutline,
                mdiCogOutline,
                mdiCreditCardOutline,
                mdiLogout,
                isLoggedIn,
                isDarkMode,
                userProfile,
                userStore: userState,
                authPrompt,
                unreadCount,
                notificationState,
                postLoginLanding
            };
        }
    }
</script>

<style scoped>
.core-google-login {
    display: inline-flex;
    align-items: center;
}

.core-google-login--provider {
    width: 100%;
}

.core-google-login__shell {
    position: relative;
    display: inline-flex;
    width: 100%;
    min-width: 0;
    height: 46px;
    overflow: hidden;
    border-radius: 10px;
}

.core-google-login__signin-btn {
    min-width: 112px;
    height: 40px !important;
    padding-inline: 14px !important;
    border-color: rgba(var(--v-theme-primary), 0.68) !important;
    background: rgba(var(--v-theme-primary), 0.04) !important;
    box-shadow: none !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    transition: background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease !important;
}

.core-google-login__signin-btn :deep(.v-btn__prepend) {
    margin-inline-end: 7px;
}

.core-google-login__signin-btn :deep(.v-icon) {
    width: 21px;
    height: 21px;
}

.core-google-login__signin-btn:hover {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.1) !important;
}

.core-google-login__signin-btn:focus-visible {
    border-color: rgb(var(--v-theme-primary)) !important;
    box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.22) !important;
}

.core-google-login--drawer .core-google-login__signin-btn {
    max-width: 240px;
    margin-inline: auto;
}

.core-google-login__visual {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 100%;
    padding: 0 15px;
    border-radius: inherit;
    border: 1px solid #747775;
    background: #ffffff;
    box-shadow: none;
    color: #1f1f1f;
    transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.core-google-login__icon-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    box-shadow: none;
    flex: 0 0 auto;
}

.core-google-login__icon-svg {
    width: 20px;
    height: 20px;
}

.core-google-login__label {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0;
    white-space: nowrap;
    font-family: Roboto, Arial, sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    flex: 1 1 auto;
    min-width: 0;
    padding-inline-end: 30px;
}

.core-google-login__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 400px;
    height: 40px;
    transform-origin: top left;
    z-index: 2;
    border-radius: inherit;
    opacity: 0.001;
}

.core-google-login__overlay :deep(.g-btn-wrapper),
.core-google-login__overlay :deep(.g-btn) {
    display: block;
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
    min-height: 100% !important;
}

.core-google-login__shell:focus-within {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
}

.core-google-login__shell:hover .core-google-login__visual {
    background: #f8faff;
    box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3);
}

.core-google-login__avatar-btn {
    position: relative;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    margin-top: 0 !important;
    overflow: visible !important;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
}

.core-google-login__avatar-btn:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 3px;
}

.core-google-login__avatar {
    font-weight: 800;
    letter-spacing: 0;
    overflow: hidden;
}

.core-google-login__avatar-image {
    width: 100%;
    height: 100%;
    background: rgb(var(--v-theme-surface-bright));
}

.core-google-login__avatar-image :deep(.v-img__img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.core-google-login__avatar-image--character :deep(.v-img__img) {
    transform: none;
}

.core-google-login__avatar--menu {
    flex: 0 0 auto;
}

.core-google-login__menu {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.14);
    border-radius: 14px !important;
    background: rgb(var(--v-theme-surface));
    color: rgb(var(--v-theme-on-surface));
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18) !important;
}

.core-google-login__identity {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 18px 16px;
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.core-google-login__identity-copy {
    min-width: 0;
}

.core-google-login__name {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.core-google-login__email {
    margin: 3px 0 0;
    color: rgba(var(--v-theme-on-surface), 0.65);
    font-size: 0.75rem;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.core-google-login__actions {
    display: grid;
    gap: 2px;
    padding: 8px;
}

.core-google-login__logout {
    padding: 8px;
    border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.core-google-login__menu-btn {
    justify-content: flex-start !important;
    min-height: 42px;
    padding-inline: 12px !important;
    border-radius: 8px !important;
    font-size: 0.8125rem;
    font-weight: 500;
    letter-spacing: 0 !important;
    text-transform: none !important;
}

.core-google-login__menu-btn :deep(.v-btn__prepend) {
    margin-inline-end: 12px;
    color: rgba(var(--v-theme-on-surface), 0.58);
}

.core-google-login__menu-btn :deep(.v-icon) {
    width: 19px;
    height: 19px;
}

.core-google-login__menu-btn :deep(.v-btn__content) {
    justify-content: space-between;
    width: 100%;
}

.core-google-login__menu-btn:hover,
.core-google-login__menu-btn:focus-visible {
    color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-primary), 0.06);
}

.core-google-login__menu-btn:hover :deep(.v-btn__prepend),
.core-google-login__menu-btn:focus-visible :deep(.v-btn__prepend) {
    color: rgb(var(--v-theme-primary));
}

.core-google-login__menu-btn:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
}

.core-google-login__announcement-badge {
    position: absolute;
    top: -3px;
    right: -5px;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 22px;
    height: 18px;
    padding: 0 5px;
    border: 2px solid rgb(var(--v-theme-surface));
    border-radius: 9px;
    background: rgb(var(--v-theme-primary));
    color: #071a2e;
    font-size: 0.65rem;
    font-weight: 800;
    line-height: 1;
    letter-spacing: 0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
}

.core-google-login__menu-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background: rgba(var(--v-theme-primary), 0.16);
    color: rgb(var(--v-theme-primary));
    font-size: 0.7rem;
    font-weight: 800;
}

.core-google-login--drawer {
    display: flex;
    justify-content: center;
    width: 100%;
}
</style>
